import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight, Footprints } from 'lucide-react';
import { formatCategoryName } from '../utils/category-format';

const SLIDE_DURATION = 6.5; // seconds, also drives the progress bar
const MAX_SLIDES = 6;
const SWIPE_THRESHOLD = 50;
const EASE = [0.22, 1, 0.36, 1];

// Editorial copy per collection, matched on the category name; order = slide order.
// Titles are two short lines so the buttons below never jump between slides.
const COLLECTIONS = [
  {
    keywords: ['sandale'],
    kicker: 'Sandales orthopédiques',
    title: ['Légèreté', 'et maintien.'],
    text: 'Des brides souples et une semelle anatomique qui soutient la voûte plantaire, du matin au soir.',
  },
  {
    keywords: ['claquette', 'mule'],
    kicker: 'Mules & claquettes',
    title: ['Glissez-vous', 'dans le confort.'],
    text: 'Faciles à enfiler, élégantes à porter : des modèles pensés pour soulager chaque pas.',
  },
  {
    keywords: ['sabot'],
    kicker: 'Sabots orthopédiques',
    title: ['Un maintien', 'tout en douceur.'],
    text: 'Une semelle qui amortit et soutient le pied, pour les journées les plus longues.',
  },
  {
    keywords: ['basket', 'espadrille'],
    kicker: 'Baskets & espadrilles',
    title: ['Le confort', 'en mouvement.'],
    text: 'Des modèles légers et stables pour marcher sans fatigue, en ville comme en voyage.',
  },
  {
    keywords: ['mocassin', 'derbie'],
    kicker: 'Mocassins & derbies',
    title: ['L’élégance', 'sans compromis.'],
    text: 'Des chaussures raffinées pour le bureau comme pour le week-end.',
  },
  {
    keywords: ['bottine', 'botte'],
    kicker: 'Bottines & bottes',
    title: ['L’hiver,', 'en douceur.'],
    text: 'Des bottines élégantes et confortables pour traverser l’hiver en douceur.',
  },
];

const DEFAULT_COPY = {
  title: ['Le confort', 'à chaque pas.'],
  text: 'Des chaussures pensées pour la femme : soutien, douceur et élégance au quotidien.',
};

const collectionFor = (name = '') => {
  const lower = name.toLowerCase();
  const rank = COLLECTIONS.findIndex(c => c.keywords.some(keyword => lower.includes(keyword)));
  return rank === -1
    ? { rank: COLLECTIONS.length, ...DEFAULT_COPY, kicker: formatCategoryName(name) }
    : { rank, ...COLLECTIONS[rank] };
};

const pad = (n) => String(n).padStart(2, '0');

const copyVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
  exit: { opacity: 0, y: -12, transition: { duration: 0.3 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};
const lineVariants = {
  hidden: { y: '105%' },
  visible: { y: 0, transition: { duration: 0.8, ease: EASE } },
};

// Shown when the catalogue has no category photos (or the API is unreachable)
const BrandPanel = () => (
  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#174540] to-[#0c2a27] text-center">
    <Footprints className="h-10 w-10 text-[#c9a078]" strokeWidth={1.3} />
    <span className="mt-6 font-serif text-6xl italic text-white">Tendresse</span>
    <span className="mt-4 text-[0.65rem] font-semibold uppercase tracking-[0.35em] text-[#e6d3bd]">
      Chaussures orthopédiques femme
    </span>
  </div>
);

const HeroSection = ({ showcase = [], pending = false, onSelectCategory }) => {
  const slides = useMemo(
    () =>
      showcase
        .filter(item => item?.imagePath)
        .map(item => ({ id: item.id, name: item.name, image: item.imagePath, ...collectionFor(item.name) }))
        .sort((a, b) => a.rank - b.rank)
        .slice(0, MAX_SLIDES),
    [showcase]
  );

  const [index, setIndex] = useState(0);
  const count = slides.length;
  const current = count ? index % count : 0;
  const active = slides[current];
  const upcoming = count > 1 ? slides[(current + 1) % count] : null;
  const copy = active ?? { ...DEFAULT_COPY, kicker: 'Chaussures femme' };

  const goTo = useCallback((i) => setIndex(count ? (i + count) % count : 0), [count]);
  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  // Warm the cache for the next photo so the crossfade never reveals an empty panel
  useEffect(() => {
    if (!upcoming) return;
    const img = new Image();
    img.src = upcoming.image;
  }, [upcoming]);

  const shop = () => {
    if (active?.id != null && typeof onSelectCategory === 'function') {
      onSelectCategory(active.id);
      return;
    }
    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Touch swipe on the photo
  const touchStartX = useRef(null);
  const handlePointerDown = (e) => {
    if (e.pointerType === 'touch') touchStartX.current = e.clientX;
  };
  const handlePointerUp = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.clientX - touchStartX.current;
    touchStartX.current = null;
    if (delta <= -SWIPE_THRESHOLD) next();
    else if (delta >= SWIPE_THRESHOLD) prev();
  };

  return (
    <MotionConfig reducedMotion="user">
      <section
        className="tdr-hero relative overflow-hidden bg-[#f6f1ea]"
        aria-roledescription="carrousel"
        aria-label="Nos collections"
        style={{ '--tdr-slide-duration': `${SLIDE_DURATION}s` }}
      >
        <div className="mx-auto grid max-w-7xl gap-8 px-4 pb-14 pt-6 sm:px-6 lg:h-[calc(100vh-5rem)] lg:max-h-[860px] lg:min-h-[640px] lg:grid-cols-12 lg:gap-14 lg:px-8 lg:py-10">
          {/* Copy */}
          <div className="order-2 flex flex-col justify-center lg:order-1 lg:col-span-5">
            <div className="flex items-center gap-3 text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-[#3d8361]">
              <span className="h-px w-8 bg-[#3d8361]/50" />
              Tendresse · Chaussures femme
            </div>

            <div className="relative mt-6 flex min-h-[14.5rem] flex-col justify-end sm:min-h-[18.5rem] lg:h-[18.5rem]" aria-live="polite">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div key={active?.id ?? 'brand'} variants={copyVariants} initial="hidden" animate="visible" exit="exit">
                  <motion.p variants={itemVariants} className="text-xs font-semibold uppercase tracking-[0.25em] text-[#b07a4f]">
                    {copy.kicker}
                  </motion.p>
                  <h1 className="mt-4 font-serif text-[2.6rem] font-medium leading-[1.02] tracking-tight text-[#0c2a27] sm:text-6xl xl:text-[4.25rem]">
                    {copy.title.map((line, i) => (
                      <span key={line} className="block overflow-hidden pb-1">
                        <motion.span variants={lineVariants} className={`block ${i === 1 ? 'italic text-[#174540]' : ''}`}>
                          {line}
                        </motion.span>
                      </span>
                    ))}
                  </h1>
                  <motion.p variants={itemVariants} className="mt-6 max-w-md text-base font-light leading-relaxed text-[#3a4a47] sm:text-lg">
                    {copy.text}
                  </motion.p>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={shop}
                className="group inline-flex items-center gap-3 rounded-full bg-[#174540] px-7 py-4 text-sm font-semibold tracking-wide text-white shadow-[0_14px_30px_-14px_rgba(23,69,64,0.8)] transition-colors duration-300 hover:bg-[#0c2a27] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#174540]/25"
              >
                {active ? 'Acheter la collection' : 'Découvrir la collection'}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <a
                href="#products"
                className="inline-flex items-center rounded-full border border-[#0c2a27]/15 px-7 py-4 text-sm font-semibold tracking-wide text-[#0c2a27] transition-colors duration-300 hover:border-[#0c2a27]/40 hover:bg-white"
              >
                Tous les produits
              </a>
            </div>

            {count > 1 && (
              <div className="mt-10 flex items-center gap-5 sm:mt-12">
                <span className="font-serif text-lg tabular-nums text-[#0c2a27]">
                  {pad(current + 1)}
                  <span className="text-[#0c2a27]/35"> / {pad(count)}</span>
                </span>

                <div className="flex flex-1 gap-2" role="tablist" aria-label="Choisir une collection">
                  {slides.map((slide, i) => (
                    <button
                      key={slide.id}
                      type="button"
                      role="tab"
                      aria-selected={i === current}
                      aria-label={slide.kicker}
                      title={slide.kicker}
                      onClick={() => goTo(i)}
                      className="group relative h-8 flex-1 focus:outline-none"
                    >
                      <span className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 overflow-hidden rounded-full bg-[#0c2a27]/10 transition-colors group-hover:bg-[#0c2a27]/25 group-focus-visible:bg-[#0c2a27]/30">
                        {i < current && <span className="absolute inset-0 bg-[#174540]/40" />}
                        {i === current && (
                          <span key={`progress-${current}`} className="tdr-progress absolute inset-0 bg-[#174540]" onAnimationEnd={next} />
                        )}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={prev}
                    aria-label="Collection précédente"
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-[#0c2a27]/15 text-[#0c2a27] transition-colors hover:border-[#0c2a27]/40 hover:bg-white"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    aria-label="Collection suivante"
                    className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0c2a27] text-white transition-colors hover:bg-[#174540]"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Photo */}
          <div
            className="tdr-hero-visual relative order-1 aspect-square touch-pan-y overflow-hidden rounded-[1.75rem] bg-[#ece2d4] lg:order-2 lg:col-span-7 lg:aspect-auto"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerCancel={() => { touchStartX.current = null; }}
          >
            {pending ? (
              <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-[#efe4d6] to-[#e2cfb8]" />
            ) : !active ? (
              <BrandPanel />
            ) : (
              <AnimatePresence initial={false}>
                <motion.img
                  key={active.image}
                  src={active.image}
                  alt={formatCategoryName(active.name)}
                  draggable={false}
                  className="absolute inset-0 h-full w-full select-none object-cover"
                  initial={{ opacity: 0, scale: 1.06 }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                    transition: { opacity: { duration: 0.9 }, scale: { duration: SLIDE_DURATION + 1, ease: 'linear' } },
                  }}
                  exit={{ opacity: 0, transition: { duration: 0.9 } }}
                />
              </AnimatePresence>
            )}

            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0c2a27]/15 to-transparent" />

            {active && (
              <span className="absolute left-4 top-4 rounded-full bg-white/85 px-4 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.25em] text-[#174540] backdrop-blur sm:left-6 sm:top-6">
                {copy.kicker}
              </span>
            )}

            {upcoming && (
              <button
                type="button"
                onClick={next}
                className="group absolute bottom-6 right-6 hidden items-center gap-3 rounded-2xl bg-white/90 p-2 pr-4 text-left shadow-[0_20px_40px_-20px_rgba(12,42,39,0.5)] backdrop-blur transition-colors hover:bg-white sm:flex"
              >
                <img src={upcoming.image} alt="" className="h-14 w-14 rounded-xl object-cover" />
                <span>
                  <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.25em] text-[#3d8361]">Suivant</span>
                  <span className="block text-sm font-semibold text-[#0c2a27]">{upcoming.kicker}</span>
                </span>
                <ChevronRight className="h-4 w-4 text-[#0c2a27] transition-transform group-hover:translate-x-0.5" />
              </button>
            )}
          </div>
        </div>
      </section>
    </MotionConfig>
  );
};

export default HeroSection;
